import * as THREE from 'three';
import { HEAT_START_FRAC } from './heat';
import { TEST_SWITCHES, urlParam } from '../urlFlags.ts';

// Hot radiators glow. Ratified 2026-09-30 (docs/SHIP-ART-DIRECTION.md §1): a
// pumped liquid-metal loop feeds refractory panels at ~950 K, past the Draper
// point (~800 K), so a deployed wing at cruise reads dull cherry red and a
// stowed one fades to its coating as it cools.
//
// Everything here is physics first and one exposure constant second:
//
//   COLOUR    - Planck's law integrated against the CIE 1931 2-degree observer
//               (the CIE table itself), XYZ to linear sRGB.
//               At 950 K the locus lies outside sRGB past its red primary.
//               Clipping the negative channels there gives a flat (1, 0, 0)
//               that reads as paint; instead the colour is pulled toward the
//               neutral axis at constant luminance just far enough that no
//               channel falls under RADIATOR.gamutFloor of the luminance - the
//               deep ember a camera records, not a signal-red fill.
//   STRUCTURE - a panel is never a flat fill: the root-hot gradient, coolant
//               channels running root to tip a few kelvin hotter than the web
//               between them, and occasional slugs of hotter coolant travelling
//               out along a channel (a few percent to RADIATOR.rippleAmp brighter,
//               the crest tinted toward a hotter blackbody),
//               each channel on its own irregular clock, more and faster the
//               more heat the panel is rejecting, none when it is off the loop.
//   BRIGHTNESS - the same integral's luminance, relative to the design point.
//               NOT sigma T^4: that is the total, almost all of it infrared.
//               What the eye sees falls far faster - about 650x from 950 K to
//               750 K - which is why a panel just below the Draper point is
//               simply dark rather than faintly pink.
//   EXPOSURE  - one number, RADIATOR.designLuminance: the scene-linear
//               luminance a 950 K face emits, set against the game's ACES
//               exposure 1.0 and its bloom threshold (0.72, scene/postFx.ts):
//               0.022 reads as a deep ember at the chase distance, low in
//               the tone curve's toe where the channel lines and the slugs
//               still read as structure rather than being flattened by the
//               shoulder into a signal-red fill (0.05, round 1, read as
//               painted cardboard). The soft shoulder caps anything the loop
//               can reach at 7x that (0.15), still under the threshold,
//               so a 1400 K dump reads oranger and brighter rather than 3000x
//               brighter on a display that cannot show it.
//   KIRCHHOFF - the emission is weighted by (1 - albedo) per channel: a face
//               that reflects well emits poorly. That is what keeps the
//               coating's albedo under the glow and what stops a pale coating
//               washing pink - the failure the old "never glow" rule was
//               written against.
//   TEMPERATURE - read, never written, off the hull's own HeatState. The sim's
//               rejection is linear in stored heat and a panel radiates
//               eps sigma A T^4, so the loop temperature consistent with the
//               sim's own rejection per panel is T = T_design (f / f_design)^(1/4).
//               f_design is the start fraction, the as-built nominal, which is
//               also the Tower's idle equilibrium (0.179). A hull with more
//               panels sheds the same load at a lower f and runs cooler; a
//               heat-soaked hull that redeploys dumps it orange.
//               Stowed panels come off the loop and cool by radiation alone,
//               the exact T^-3 solution of dT/dt = -k T^4.

export const RADIATOR = {
  /** Panel face temperature at the design point (canon: ~950 K). */
  designK: 950,
  /** Stored heat fraction the design point sits at. */
  designFrac: HEAT_START_FRAC,
  /** Refractory ceiling: the loop never drives a face past this. */
  maxK: 1500,
  /** A stowed panel soaked to the hull's own temperature. */
  coldK: 290,
  /** Seconds for the pumped loop to bring a deployed face to loop temperature. */
  loopTauS: 2.0,
  /**
   * Radiative cooling constant 2 eps sigma / (rho c delta), K^-3 s^-1: both
   * faces at eps 0.9 over 5.1 kJ/m2K of areal heat capacity (about 2 mm of
   * refractory sheet), so a stowed panel falls 950 -> 800 K in ~13 s and
   * 950 -> 750 K in ~20 s.
   */
  coolK3: 2.0e-11,
  /** Fraction the face temperature falls per unit of the heat attribute, inlet (1) to outlet (0). */
  rootTipDrop: 0.12,
  /** No channel falls under this fraction of the luminance after gamut mapping. */
  gamutFloor: 0.07,
  /** Coolant channels: fraction hotter than the web between them, and half-width of a channel as a fraction of the pitch. */
  channelBump: 0.015,
  channelHalfWidth: 0.14,
  /** A coolant slug's peak brightening at full flux at the `boost` level, the ladder's bottom rung. */
  rippleAmp: 0.6,
  /** Blackbody temperature a slug's crest is tinted toward, K: a hotter slug reads warmer. */
  rippleWarmK: 1050,
  /** Span fractions a slug travels per second at design flux. */
  rippleSpeed: 0.12,
  /** Heat attribute the panel temperature is quoted at, and where a face with no gradient sits. */
  meanHeat: 0.6,
  /** Decay of the heat attribute along a wing's span (tools/parts/build_parts.py paint_heat). */
  spanDecay: 1.5,
  /** Scene-linear luminance a 950 K face with albedo 0 emits. */
  designLuminance: 0.022,
  /** Soft shoulder: emitted luminance never exceeds (1 + shoulder) x the design. */
  shoulder: 6,
  /** The LUT the shader samples, K. */
  lutMinK: 500,
  lutMaxK: 1700,
  lutSize: 256,
} as const;

// ---- Planck x CIE 1931 ----
const C1 = 1.191042972e-16;   // 2hc^2, W m^2 / sr
const C2 = 1.438776877e-2;    // hc/k, m K
const KM = 683;               // lm / W

// CIE 1931 2-degree colour matching functions, 380-780 nm every 10 nm (CIE
// 015:2018 table). Not an analytic fit: the usual multi-lobe Gaussian falls off
// twice too fast past 680 nm, which is exactly where a 950 K body puts its
// light, and moves the whole locus 0.01 toward green.
const CMF: readonly (readonly [number, number, number])[] = [
  [0.001368, 0.000039, 0.006450], [0.004243, 0.000120, 0.020050], [0.014310, 0.000396, 0.067850],
  [0.043510, 0.001210, 0.207400], [0.134380, 0.004000, 0.645600], [0.283900, 0.011600, 1.385600],
  [0.348280, 0.023000, 1.747060], [0.336200, 0.038000, 1.772110], [0.290800, 0.060000, 1.669200],
  [0.195360, 0.090980, 1.287640], [0.095640, 0.139020, 0.812950], [0.032010, 0.208020, 0.465180],
  [0.004900, 0.323000, 0.272000], [0.009300, 0.503000, 0.158200], [0.063270, 0.710000, 0.078250],
  [0.165500, 0.862000, 0.042160], [0.290400, 0.954000, 0.020300], [0.433450, 0.994950, 0.008750],
  [0.594500, 0.995000, 0.003900], [0.762100, 0.952000, 0.002100], [0.916300, 0.870000, 0.001650],
  [1.026300, 0.757000, 0.001100], [1.062200, 0.631000, 0.000800], [1.002600, 0.503000, 0.000340],
  [0.854450, 0.381000, 0.000190], [0.642400, 0.265000, 0.000050], [0.447900, 0.175000, 0.000020],
  [0.283500, 0.107000, 0], [0.164900, 0.061000, 0], [0.087400, 0.032000, 0],
  [0.046770, 0.017000, 0], [0.022700, 0.008210, 0], [0.011359, 0.004102, 0],
  [0.005790, 0.002091, 0], [0.002899, 0.001047, 0], [0.001440, 0.000520, 0],
  [0.000690, 0.000249, 0], [0.000332, 0.000120, 0], [0.000166, 0.000060, 0],
  [0.000083, 0.000030, 0], [0.000042, 0.000015, 0],
];
const CMF_MIN_NM = 380, CMF_STEP_NM = 10;
/** CIE 1931 2-degree colour matching functions at `nm`: the table, interpolated, halving every 10 nm past 780. */
export function cie1931(nm: number): [number, number, number] {
  const last = CMF.length - 1;
  const u = (nm - CMF_MIN_NM) / CMF_STEP_NM;
  if (u < 0) return [0, 0, 0];
  if (u >= last) {
    const k = 0.5 ** (u - last);
    const e = CMF[last]!;
    return [e[0] * k, e[1] * k, e[2] * k];
  }
  const i = Math.floor(u), f = u - i;
  const a = CMF[i]!, b = CMF[i + 1]!;
  // log-linear where both ends are positive: the red tail is an exponential
  const mix = (p: number, q: number): number => (p > 0 && q > 0 ? p * (q / p) ** f : p + (q - p) * f);
  return [mix(a[0], b[0]), mix(a[1], b[1]), mix(a[2], b[2])];
}
/** Planck spectral radiance, W / (sr m^2 m), at wavelength `m` metres. */
export function planck(m: number, tempK: number): number {
  return C1 / (m ** 5 * (Math.exp(C2 / (m * tempK)) - 1));
}
/** Blackbody XYZ at `tempK`; Y is luminance in cd/m2. */
export function blackbodyXYZ(tempK: number): [number, number, number] {
  let x = 0, y = 0, z = 0;
  const step = 1;
  for (let nm = 380; nm <= 830; nm += step) {
    const b = planck(nm * 1e-9, tempK) * step * 1e-9;
    const [xb, yb, zb] = cie1931(nm);
    x += b * xb; y += b * yb; z += b * zb;
  }
  return [KM * x, KM * y, KM * z];
}
/** Chromaticity (x, y) of the Planckian locus at `tempK`. */
export function blackbodyXy(tempK: number): [number, number] {
  const [x, y, z] = blackbodyXYZ(tempK);
  const s = x + y + z;
  return [x / s, y / s];
}

const REC709_Y = [0.2126, 0.7152, 0.0722] as const;
/** Linear sRGB of a blackbody at `tempK`, scaled to luminance 1, pulled into gamut toward neutral at constant luminance. */
export function blackbodyRgb(tempK: number): [number, number, number] {
  const [x, y, z] = blackbodyXYZ(tempK);
  if (!(y > 0)) return [0, 0, 0];
  // XYZ's Y is the Rec.709 luminance of this rgb, so dividing by it gives luminance 1
  const c = [
    (3.2406 * x - 1.5372 * y - 0.4986 * z) / y,
    (-0.9689 * x + 1.8758 * y + 0.0415 * z) / y,
    (0.0557 * x - 0.2040 * y + 1.0570 * z) / y,
  ];
  // mix toward (1,1,1), luminance 1, until the lowest channel reaches the floor
  const lo = Math.min(c[0]!, c[1]!, c[2]!);
  const f = RADIATOR.gamutFloor;
  const s = lo >= f ? 0 : (f - lo) / (1 - lo);
  const out = c.map((v) => v * (1 - s) + s);
  const lum = REC709_Y[0] * out[0]! + REC709_Y[1] * out[1]! + REC709_Y[2] * out[2]!;
  return [out[0]! / lum, out[1]! / lum, out[2]! / lum];
}

const designY = blackbodyXYZ(RADIATOR.designK)[1];
/** Visible luminance at `tempK` against the design point: 1 at 950 K. */
export function relativeLuminance(tempK: number): number {
  return tempK <= 0 ? 0 : blackbodyXYZ(tempK)[1] / designY;
}
/** Scene-linear luminance a face of albedo 0 emits at `tempK`, shouldered. */
export function glowLuminance(tempK: number): number {
  const y = relativeLuminance(tempK);
  const s = RADIATOR.shoulder;
  return RADIATOR.designLuminance * (1 + 1 / s) * y / (1 + y / s);
}
/** Scene-linear RGB a face of albedo 0 emits at `tempK`: what the shader adds before Kirchhoff. */
export function glowRadiance(tempK: number): [number, number, number] {
  const l = glowLuminance(tempK);
  const [r, g, b] = blackbodyRgb(tempK);
  return [r * l, g * l, b * l];
}

// ---- coolant slug strength ----
/**
 * What a slug's crest does over the web around it. The crest is coolant
 * warmK - designK hotter than the panel it crosses, so its tint is the
 * blackbody at the panel's temperature plus that excess over the blackbody at
 * the panel's, per channel: a hotter slug reads warmer, never a picked hue,
 * and a soaked panel's slug is tinted from where that panel already sits.
 * For scale, a slug truly at 1050 K over a 950 K panel would read x4.5 after
 * the shoulder and one at 1400 K x7 (the cap); every level below is dimmer
 * than the temperature its colour quotes.
 */
export interface RippleLook {
  /** Peak brightening at full flux. */
  amp: number;
  /** Crest chromaticity temperature, K. */
  warmK: number;
  /** How far the crest's colour moves toward warmK's, 0-1, at the strongest slug. */
  blend: number;
  /** Gaussian half-width of a slug, fraction of a pass. */
  width: number;
  /** Span fractions a slug travels per second at design flux. */
  speed: number;
  /** Share of passes carrying a slug at no flux; each unit of flux adds 0.2, capped at 0.85. */
  gate: number;
}
export type RippleLevel = 'boost' | 'plus' | 'plusplus' | 'max';
export const RIPPLE_LEVELS: readonly RippleLevel[] = ['boost', 'plus', 'plusplus', 'max'];
export const RIPPLE_LOOKS: Readonly<Record<RippleLevel, RippleLook>> = {
  boost: { amp: RADIATOR.rippleAmp, warmK: RADIATOR.rippleWarmK, blend: 0.5, width: 0.09, speed: RADIATOR.rippleSpeed, gate: 0.3 },
  plus: { amp: 1.2, warmK: 1150, blend: 0.65, width: 0.1, speed: 0.135, gate: 0.35 },
  plusplus: { amp: 2.4, warmK: 1250, blend: 0.8, width: 0.12, speed: 0.15, gate: 0.4 },
  // the slug as bright as its colour says: glowLuminance(1400) / glowLuminance(950) - 1
  max: { amp: 6.0, warmK: 1400, blend: 1.0, width: 0.14, speed: 0.18, gate: 0.45 },
};
/** Erik's pick (2026-09-30), from watching the ladder: the full `max`. */
export const DEFAULT_RIPPLE_LEVEL: RippleLevel = 'max';
/** Any unknown or absent level reads as the default. */
export function parseRippleLevel(raw: string | null | undefined): RippleLevel {
  return raw === 'boost' || raw === 'plus' || raw === 'plusplus' || raw === 'max' ? raw : DEFAULT_RIPPLE_LEVEL;
}
/** blackbodyRgb(warmK) over blackbodyRgb(panelK), per channel. */
export function rippleWarmTint(warmK: number, panelK: number = RADIATOR.designK): [number, number, number] {
  const w = blackbodyRgb(warmK), d = blackbodyRgb(panelK);
  return [w[0] / d[0], w[1] / d[1], w[2] / d[2]];
}
/** The default crest's tint over a panel at the design point. */
export const RIPPLE_WARM_TINT = rippleWarmTint(RADIATOR.rippleWarmK);
const warmTints = new Map<string, [number, number, number]>();
// `?ripple=boost|plus|plusplus|max` picks the slug strength under test
// switches; production always runs DEFAULT_RIPPLE_LEVEL.
let rippleLevel: RippleLevel = parseRippleLevel(TEST_SWITCHES ? urlParam('ripple') : null);
/** Switch the slug strength on every radiator from its next update; for instruments and recordings. */
export function setRippleLevel(level: RippleLevel): void {
  rippleLevel = level;
}
/** The slug strength in force. */
export function currentRippleLevel(): RippleLevel {
  return rippleLevel;
}
/** The crest tint for `level` over a panel at `panelK`, in 10 K steps (two Planck integrals each, so memoised). */
function warmTint(level: RippleLevel, panelK: number): [number, number, number] {
  const k = Math.min(RADIATOR.lutMaxK, Math.max(RADIATOR.lutMinK, Math.round(panelK / 10) * 10));
  const key = `${level}|${k}`;
  let t = warmTints.get(key);
  if (!t) {
    t = rippleWarmTint(k + RIPPLE_LOOKS[level].warmK - RADIATOR.designK, k);
    warmTints.set(key, t);
  }
  return t;
}

// ---- temperature, read off the heat model ----
/** Loop temperature the sim's stored heat implies, K: what a deployed panel settles at. */
export function loopTemperature(heatFraction: number): number {
  const f = Math.max(0, heatFraction);
  const t = RADIATOR.designK * Math.pow(f / RADIATOR.designFrac, 0.25);
  return Math.min(RADIATOR.maxK, Math.max(RADIATOR.coldK, t));
}
/**
 * One step of the panel face temperature: the deployed share on the loop
 * relaxes to loop temperature, the stowed share cools radiatively. Both
 * halves are exact solutions, so a warped frame of any length is stable.
 */
export function stepPanelTemperature(panelK: number, dt: number, deployFraction: number, loopK: number): number {
  if (!(dt > 0)) return panelK;
  const d = Math.min(1, Math.max(0, deployFraction));
  let t = loopK + (panelK - loopK) * Math.exp(-d * dt / RADIATOR.loopTauS);
  const k = (1 - d) * RADIATOR.coolK3;
  if (k > 0 && t > RADIATOR.coldK) t = Math.max(RADIATOR.coldK, Math.pow(t ** -3 + 3 * k * dt, -1 / 3));
  return t;
}
/** Seconds a stowed panel takes to cool from `fromK` to `toK` by radiation alone. */
export function coolingSeconds(fromK: number, toK: number): number {
  return (toK ** -3 - fromK ** -3) / (3 * RADIATOR.coolK3);
}

// ---- the control: what the instrument must be able to see fail ----
export type GlowInjection = 'constant' | 'none' | 'no-ripple' | 'sync-ripple' | 'stuck-level' | null;
let injected: GlowInjection = null;
let rippleMuted = false;
/**
 * `constant` pins every face to the design temperature whatever the ship is
 * doing, `none` puts the glow out, `no-ripple` stills the coolant slugs and
 * `sync-ripple` puts every channel on one clock, `stuck-level` holds every
 * radiator at `boost` whatever level is asked for. `null` is the shipped path.
 * Set by tools/preview/radiator-glow-check.mjs --inject.
 */
export function setGlowInjected(mode: GlowInjection): void {
  injected = mode;
}
/** Still the slugs while an instrument reads steady radiance; not a control. */
export function setGlowRippleMuted(on: boolean): void {
  rippleMuted = on;
}

// ---- the shader ----
let lut: THREE.DataTexture | null = null;
/** The shared temperature -> glow table, one per page, never disposed (like the shared ship textures). */
function glowLut(): THREE.DataTexture {
  if (lut) return lut;
  const n = RADIATOR.lutSize;
  const data = new Uint16Array(n * 4);
  for (let i = 0; i < n; i++) {
    const k = RADIATOR.lutMinK + (RADIATOR.lutMaxK - RADIATOR.lutMinK) * (i / (n - 1));
    const [r, g, b] = glowRadiance(k);
    data[i * 4] = THREE.DataUtils.toHalfFloat(r);
    data[i * 4 + 1] = THREE.DataUtils.toHalfFloat(g);
    data[i * 4 + 2] = THREE.DataUtils.toHalfFloat(b);
    data[i * 4 + 3] = THREE.DataUtils.toHalfFloat(1);
  }
  lut = new THREE.DataTexture(data, n, 1, THREE.RGBAFormat, THREE.HalfFloatType);
  lut.magFilter = lut.minFilter = THREE.LinearFilter;
  lut.wrapS = lut.wrapT = THREE.ClampToEdgeWrapping;
  lut.needsUpdate = true;
  return lut;
}

/** Face temperature at heat-attribute value `heat`, for a panel quoted at `panelK`. */
export function faceTemperature(panelK: number, heat: number = RADIATOR.meanHeat): number {
  return panelK * (1 + RADIATOR.rootTipDrop * (heat - RADIATOR.meanHeat));
}

/** Heat attribute at fraction `s` of a wing's span out from its root, the same curve rad-panel's COLOR_0 carries. */
export function spanHeat(s: number): number {
  return Math.exp(-RADIATOR.spanDecay * Math.min(1, Math.max(0, s)));
}

export interface RadiatorGlow {
  /**
   * Per frame: the panel temperature (K, the face temperature at the mean
   * point of the run), the clock the slugs run on (s) and the deploy fraction
   * - on the loop or off it.
   */
  update(kelvin: number, timeS: number, deployFraction: number): void;
  /** Temperature alone, for a caller with no clock (a plate, a restore). */
  setTemperature(kelvin: number): void;
  /**
   * For a mesh with no COLOR_0 (an authored hull's wings): the ship-frame
   * lateral distances of the wing root and tip, and the mesh's own fixed
   * transform into the ship frame, so the face reads the loop's root-hot
   * gradient off |x| the way a forged panel reads it off COLOR_0.
   */
  setSpan(rootX: number, tipX: number, meshToShip: THREE.Matrix4): void;
  /**
   * Give a mesh drawn with this material its own slug clock: many forged
   * panels share one material and one geometry, so the mesh is moved onto a
   * clone of the material that differs only in its seed. The caller's
   * dispose() frees the clone with the mesh's other materials.
   */
  seedMesh(mesh: THREE.Object3D, seed: number): void;
  /**
   * Instrument read-out: the shader writes (heat, 1, channel) as its only
   * light instead of the glow, so a probe can learn each pixel's heat
   * reading and channel coverage from the same code that draws it.
   */
  setProbe(on: boolean): void;
  /** What the shader is being given, after any control. */
  readonly kelvin: number;
  /** Where the face temperature gradient comes from. */
  readonly gradient: 'vertex' | 'span' | 'none';
  /** What setSpan was given, or null. */
  readonly span: { root: number; tip: number; toShip: THREE.Matrix4 } | null;
  /** The coolant channels: across-channel axis and pitch, in the frame `toShip` maps the mesh into. */
  readonly channel: { axis: THREE.Vector3; pitch: number; toShip: THREE.Matrix4 };
}

/**
 * Chain the glow onto a material's existing onBeforeCompile (triplanar, and
 * hull damage after it). The material's own emissive is left to the caller;
 * set it black so the glow is the only emission a radiator carries.
 */
export function applyRadiatorGlow(mat: THREE.MeshStandardMaterial): RadiatorGlow {
  const uniforms = {
    uRadK: { value: 0 },
    uRadLut: { value: glowLut() },
    uRadRange: { value: new THREE.Vector3(RADIATOR.lutMinK, RADIATOR.lutMaxK, RADIATOR.lutSize) },
    uRadDrop: { value: RADIATOR.rootTipDrop },
    uRadMeanHeat: { value: RADIATOR.meanHeat },
    uRadSpan: { value: new THREE.Vector3(0, 0, RADIATOR.spanDecay) },
    uRadToShip: { value: new THREE.Matrix4() },
    // across-channel axis (xyz) and channel pitch (w), in the frame uRadToShip maps to
    uRadChan: { value: new THREE.Vector4(1, 0, 0, 0.074) },
    uRadChanBump: { value: new THREE.Vector2(RADIATOR.channelBump, RADIATOR.channelHalfWidth) },
    // time, flux (T/T_design)^4 x deploy, slug amplitude, 1 = one clock for all
    uRadRipple: { value: new THREE.Vector4(0, 0, RADIATOR.rippleAmp, 0) },
    // slug speed, half-width, share of passes carrying one at no flux, crest tint blend
    uRadSlug: { value: new THREE.Vector4(RADIATOR.rippleSpeed, 0.09, 0.3, 0.5) },
    uRadWarmTint: { value: new THREE.Vector3(...RIPPLE_WARM_TINT) },
    uRadSeed: { value: 0 },
    uRadProbe: { value: 0 },
  };
  const prev = mat.onBeforeCompile.bind(mat);
  const prevKey = mat.customProgramCacheKey();
  mat.onBeforeCompile = (shader, renderer) => {
    prev(shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
uniform mat4 uRadToShip;
varying vec3 vRadS;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
vRadS = ( uRadToShip * vec4( transformed, 1.0 ) ).xyz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uRadK;
uniform sampler2D uRadLut;
uniform vec3 uRadRange;
uniform float uRadDrop;
uniform float uRadMeanHeat;
uniform vec3 uRadSpan;
uniform vec4 uRadChan;
uniform vec2 uRadChanBump;
uniform vec4 uRadRipple;
uniform vec4 uRadSlug;
uniform vec3 uRadWarmTint;
uniform float uRadSeed;
uniform float uRadProbe;
varying vec3 vRadS;
float radHash( vec2 p ) {
	return fract( sin( dot( p, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 );
}`)
      .replace('#include <lights_physical_fragment>', `{
	// where on the coolant run this face is: radH the heat reading (1 at the
	// inlet), radS the span fraction out from the root
	float radH = uRadMeanHeat;
	float radS = 0.5;
#ifdef USE_COLOR
	radH = vColor.r;
	radS = clamp( -log( max( radH, 1e-3 ) ) / uRadSpan.z, 0.0, 1.0 );
#else
	if ( uRadSpan.y > uRadSpan.x ) {
		radS = clamp( ( abs( vRadS.x ) - uRadSpan.x ) / ( uRadSpan.y - uRadSpan.x ), 0.0, 1.0 );
		radH = exp( -uRadSpan.z * radS );
	}
#endif
	// coolant channels run root to tip; at range they fade to their mean
	// coverage rather than alias into moire
	float radU = dot( vRadS, uRadChan.xyz ) / uRadChan.w;
	float radCi = floor( radU );
	float radAa = fwidth( radU );
	float radLine = 1.0 - smoothstep( uRadChanBump.y, uRadChanBump.y + max( radAa, 1e-4 ), abs( fract( radU ) - 0.5 ) );
	radLine = mix( radLine, 2.0 * uRadChanBump.y, smoothstep( 0.25, 0.7, radAa ) );
	float radT = uRadK * ( 1.0 + uRadDrop * ( radH - uRadMeanHeat ) ) * ( 1.0 + uRadChanBump.x * radLine );
	float radL = clamp( ( radT - uRadRange.x ) / ( uRadRange.y - uRadRange.x ), 0.0, 1.0 );
	vec3 radGlow = texture2D( uRadLut, vec2( ( 0.5 + radL * ( uRadRange.z - 1.0 ) ) / uRadRange.z, 0.5 ) ).rgb;
	// below the table a face is dark, not the table's first entry
	radGlow *= step( uRadRange.x, radT );
	// Coolant slugs: each channel of each panel on its own clock, a travelling
	// bump from root to tip, gated so only some passes carry one, amplitude
	// and rate following the flux the panel is rejecting.
	{
		float flux = uRadRipple.y;
		float seed = mix( radHash( vec2( radCi + ( vRadS.x < 0.0 ? 57.0 : 0.0 ), uRadSeed ) ), 0.5, uRadRipple.w );
		float spd = uRadSlug.x * ( 0.7 + 0.6 * seed ) * ( 0.5 + 0.5 * min( flux, 3.0 ) );
		float ph = uRadRipple.x * spd + seed * 17.0 - radS;
		float n = floor( ph );
		float w = fract( ph );
		float pulse = exp( -pow( ( w - 0.5 ) / uRadSlug.y, 2.0 ) );
		float gate = step( 1.0 - clamp( uRadSlug.z + 0.2 * flux, 0.0, 0.85 ), radHash( vec2( n, seed * 13.1 + 1.7 ) ) );
		float amp = uRadRipple.z * ( 0.15 + 0.85 * radHash( vec2( n * 1.37, seed + 4.2 ) ) ) * clamp( flux, 0.0, 1.0 );
		// the crest is hotter coolant: warmer, by its own blackbody over the panel's
		float crest = uRadRipple.z > 0.0 ? clamp( amp * pulse * gate / uRadRipple.z, 0.0, 1.0 ) : 0.0;
		radGlow = mix( radGlow, radGlow * uRadWarmTint, crest * uRadSlug.w );
		radGlow *= 1.0 + amp * pulse * gate;
	}
	// Kirchhoff: what the face reflects it does not emit
	totalEmissiveRadiance += radGlow * ( 1.0 - clamp( diffuseColor.rgb, 0.0, 1.0 ) );
	if ( uRadProbe > 0.5 ) totalEmissiveRadiance = vec3( radH, 1.0, radLine );
}
#include <lights_physical_fragment>`);
  };
  mat.customProgramCacheKey = () => `${prevKey}|radglow-v3`;
  mat.needsUpdate = true;
  let kelvin = 0;
  const setTemperature = (k: number): void => {
    kelvin = injected === 'constant' ? RADIATOR.designK : injected === 'none' ? 0 : k;
    uniforms.uRadK.value = kelvin;
  };
  const glow: RadiatorGlow = {
    update(k: number, timeS: number, deployFraction: number): void {
      setTemperature(k);
      const r = uniforms.uRadRipple.value;
      r.x = timeS;
      r.y = Math.pow(kelvin / RADIATOR.designK, 4) * Math.min(1, Math.max(0, deployFraction));
      const level = injected === 'stuck-level' ? 'boost' : rippleLevel;
      const look = RIPPLE_LOOKS[level];
      r.z = injected === 'no-ripple' || rippleMuted ? 0 : look.amp;
      r.w = injected === 'sync-ripple' ? 1 : 0;
      uniforms.uRadSlug.value.set(look.speed, look.width, look.gate, look.blend);
      uniforms.uRadWarmTint.value.set(...warmTint(level, kelvin));
    },
    setTemperature,
    setSpan(rootX: number, tipX: number, meshToShip: THREE.Matrix4): void {
      uniforms.uRadSpan.value.x = rootX;
      uniforms.uRadSpan.value.y = tipX;
      uniforms.uRadToShip.value.copy(meshToShip);
      // an authored wing's channels run out along x, spaced down the ship
      uniforms.uRadChan.value.set(0, 0, 1, 0.12);
    },
    seedMesh(mesh: THREE.Object3D, seed: number): void {
      // A uniform changed between two draws of one material is not uploaded
      // again, so the mesh gets its own material: a clone sharing every
      // uniform object but the seed, on the same program (same cache key).
      const m = mesh as THREE.Mesh;
      if (!m.isMesh || m.material !== mat) return;
      const own = mat.clone();
      const seedU = { value: seed };
      own.onBeforeCompile = (shader, renderer) => {
        mat.onBeforeCompile(shader, renderer);
        shader.uniforms.uRadSeed = seedU;
      };
      own.customProgramCacheKey = mat.customProgramCacheKey;
      own.userData = { radiatorGlow: glow, radSeed: seedU };
      m.material = own;
    },
    setProbe(on: boolean): void {
      uniforms.uRadProbe.value = on ? 1 : 0;
    },
    get kelvin() { return kelvin; },
    get channel() {
      const c = uniforms.uRadChan.value;
      return { axis: new THREE.Vector3(c.x, c.y, c.z), pitch: c.w, toShip: uniforms.uRadToShip.value.clone() };
    },
    get span() {
      const u = uniforms.uRadSpan.value;
      return u.y > u.x ? { root: u.x, tip: u.y, toShip: uniforms.uRadToShip.value.clone() } : null;
    },
    get gradient() {
      return mat.vertexColors ? 'vertex' : uniforms.uRadSpan.value.y > uniforms.uRadSpan.value.x ? 'span' : 'none';
    },
  };
  mat.userData.radiatorGlow = glow;
  return glow;
}
